import { type FormEvent, useCallback, useState } from 'react';
import type { ZodType } from 'zod';
import { ApiError } from '#client/lib/api';
import { useToast } from '#client/providers/ToastProvider';

interface UseFormSubmitOptions<TInput, TResult> {
  /** The same schema the server validates with, from shared/schemas. */
  schema: ZodType<TInput>;
  /** Performs the request. Receives the parsed, coerced values. */
  submit: (values: TInput) => Promise<TResult>;
  /**
   * Runs after a successful save, with whatever the request returned.
   *
   * The result is passed through because a create usually wants to land on the
   * record it just made — registration in particular, where the next thing the
   * operator does is print the new patient's slip, and sending them back to a
   * list means searching for someone they registered four seconds ago.
   */
  onSuccess?: ((result: TResult) => void) | undefined;
  /**
   * Toasted on success. Omit where the outcome is already obvious — signing in
   * lands you on the dashboard, and a "Signed in" toast on top of that is
   * noise.
   */
  successMessage?: string | undefined;
}

interface UseFormSubmitResult {
  handleSubmit: (raw: unknown) => (event: FormEvent) => void;
  submitting: boolean;
  /** Field-level messages, from local Zod parsing or from the server. */
  errors: Record<string, string>;
  formError?: string | undefined;
  clearErrors: () => void;
}

/**
 * Shared create/update handling: validate with the shared schema, submit, map
 * server-side field errors back onto inputs, toast, then navigate.
 *
 * Success is any 2xx (a 201 Created is not a failure), and field errors come
 * from the structured error envelope rather than being parsed out of strings.
 */
export function useFormSubmit<TInput, TResult = unknown>({
  schema,
  submit,
  onSuccess,
  successMessage,
}: UseFormSubmitOptions<TInput, TResult>): UseFormSubmitResult {
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string>();

  const clearErrors = useCallback(() => {
    setErrors({});
    setFormError(undefined);
  }, []);

  const handleSubmit = useCallback(
    (raw: unknown) => (event: FormEvent) => {
      event.preventDefault();
      setFormError(undefined);

      const parsed = schema.safeParse(raw);
      if (!parsed.success) {
        const fieldErrors: Record<string, string> = {};
        for (const issue of parsed.error.issues) {
          const key = issue.path.map(String).join('.');
          fieldErrors[key] ??= issue.message;
        }
        setErrors(fieldErrors);
        setFormError('Please correct the highlighted fields.');
        return;
      }

      setErrors({});
      setSubmitting(true);

      submit(parsed.data)
        .then((result) => {
          if (successMessage) toast.success(successMessage);
          onSuccess?.(result);
        })
        .catch((cause: unknown) => {
          if (cause instanceof ApiError) {
            if (cause.fields) {
              const mapped: Record<string, string> = {};
              for (const [field, messages] of Object.entries(cause.fields)) {
                if (messages[0]) mapped[field] = messages[0];
              }
              setErrors(mapped);
            }
            setFormError(cause.message);
          } else {
            setFormError('Something went wrong. Please try again.');
          }
        })
        .finally(() => setSubmitting(false));
    },
    [schema, submit, onSuccess, successMessage, toast],
  );

  return { handleSubmit, submitting, errors, formError, clearErrors };
}
